<script lang="ts">
  /**
   * Renders Division 2 in-game color tags. Source data uses Massive's "cascade"
   * format where opening tags are not always closed:
   *   <color name="orange">+15%<color name="orange">, +20% to ...
   *   <color name="legendary_orange">90%<color name="orange">10 сек
   * Each `<color name="X">` opens a new colored run that lasts until the next
   * opening tag, an explicit `</color>`, or the end of the string.
   *
   * Parser tokenizes by both opening and closing tags and walks the segments,
   * emitting each piece with the currently-active color (or null = default).
   *
   * Usage: <RenderColorText text={talentDesc} />
   */
  interface Props { text: string | null | undefined; }
  let { text }: Props = $props();

  type Seg = { text: string; color: string | null };

  // Match either an opening <color name="X"> or a closing </color>.
  const TAG_RE = /<color\s+name=["']([a-zA-Z_0-9]+)["']\s*>|<\/color>/g;

  let segments: Seg[] = $derived.by(() => {
    const s = text ?? '';
    if (!s) return [];
    const out: Seg[] = [];
    let cursor = 0;
    let active: string | null = null;
    TAG_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = TAG_RE.exec(s)) !== null) {
      if (m.index > cursor) {
        const slice = s.slice(cursor, m.index);
        if (slice) out.push({ text: slice, color: active });
      }
      // m[1] defined → opening tag (cascade — replaces the active color);
      // undefined → closing tag (reset to default).
      active = m[1] ? m[1].toLowerCase() : null;
      cursor = m.index + m[0].length;
    }
    if (cursor < s.length) {
      const tail = s.slice(cursor);
      if (tail) out.push({ text: tail, color: active });
    }
    return out;
  });
</script>

{#each segments as seg, i (i)}{#if seg.color}<span class="ct ct-{seg.color}">{seg.text}</span>{:else}{seg.text}{/if}{/each}

<style>
  .ct { font-weight: 600; }
  .ct-orange { color: var(--orange); }
  .ct-legendary_orange { color: var(--exotic); }
  .ct-yellow { color: #ffd54f; }
  .ct-green { color: var(--green); }
  .ct-blue { color: var(--blue); }
  .ct-red { color: var(--red); }
  .ct-purple { color: var(--named); }
  /* Fallback: unknown color name */
  .ct:not(.ct-orange):not(.ct-legendary_orange):not(.ct-yellow):not(.ct-green):not(.ct-blue):not(.ct-red):not(.ct-purple) {
    color: var(--text);
  }
</style>
